import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useWorkflowsBuilderPageView } from "../../../pages/WorkflowsBuilderPage/WorkflowsBuilderPage.context";
import { useDrawerInvokers } from "@tiny/lib";
import { useConfigView } from "../../../Config.context";
import { FORM_CONSTANTS } from "./SelectRules.constants";
import { useAudienceData } from "./AudienceSelector/AudienceSelector.hooks";
import { useActivityData } from "./ActivitySelector/ActivitySelector.hooks";
import { AudienceOption } from "./../TriggerForm.types";
import { TriggerData } from "../../../pages/WorkflowsBuilderPage/WorkflowsBuilder/types";

export interface FormValues {
  ruleType: string;
  source: string;
  event: string;
  from: string;
  audiences: AudienceOption[];
  activityType: string;
  activityId: string;
}

const useSelectRules = ({ id }: { id: string }) => {
  const { updateNode, getNode, setWorkflowData, viewMode } =
    useWorkflowsBuilderPageView();
  const { hideDrawer } = useDrawerInvokers();
  const { config } = useConfigView();

  const node = getNode(id);
  const isTinyEmail = config?.appName === "tinyEmail";
  const isTinyAlbert = config?.appName === "tinyAlbert";

  const defaultValues: FormValues = {
    ruleType: node?.data?.ruleType || "",
    source: node?.data?.source || "",
    event: node?.data?.event || "",
    from:
      node?.data?.ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY
        ? FORM_CONSTANTS.FROM_VALUES.ANYTIME
        : FORM_CONSTANTS.FROM_VALUES.START_OF_WORKFLOW,
    audiences: [],
    activityType: node?.data?.activityType || "",
    activityId: node?.data?.activityId || "",
  };

  const methods = useForm<FormValues>({ defaultValues, mode: "onSubmit" });
  const { control, handleSubmit, setValue, getValues, reset } = methods;
  const formValues = useWatch({ control });

  const audienceData = useAudienceData({
    node,
    isTinyEmail,
    isTinyAlbert,
    setValue,
  });

  const activityData = useActivityData({
    node,
    isTinyEmail,
    isTinyAlbert,
    setValue,
    ruleType: formValues.ruleType || "",
  });

  const { onSubmit, saveRulesDisabled } = useFormSubmission({
    node,
    formValues,
    updateNode,
    hideDrawer,
    isTinyEmail,
    id,
  });

  const goBack = () => {
    setWorkflowData((prev) => ({
      ...prev,
      triggerType: undefined,
    }));
    updateNode({ id, data: {} });
  };

  React.useEffect(() => {
    if (formValues.ruleType && formValues.ruleType !== node?.data?.ruleType) {
      const resetDefaults = {
        source: "",
        event: "",
        from:
          formValues.ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY
            ? FORM_CONSTANTS.FROM_VALUES.ANYTIME
            : FORM_CONSTANTS.FROM_VALUES.START_OF_WORKFLOW,
        audiences: [],
        activityType: "",
        activityId: "",
      };

      reset({
        ...getValues(),
        ...resetDefaults,
        ruleType: formValues.ruleType,
      });
    }
  }, [formValues.ruleType, reset, getValues, node?.data?.ruleType]);

  React.useEffect(() => {
    if (
      formValues.activityType !== node?.data?.activityType &&
      node?.data?.activityType
    ) {
      const resetDefaults = {
        activityId: "",
        event: "",
      };

      reset({
        ...getValues(),
        ...resetDefaults,
        ruleType: formValues.ruleType,
      });
    }
  }, [formValues.activityType, setValue, node?.data?.activityType]);

  return {
    methods,
    control,
    formValues,
    goBack,
    onSubmit: handleSubmit(onSubmit),
    viewMode,
    hideDrawer,
    saveRulesDisabled,
    ...audienceData,
    activities: activityData.activities,
    handleActivitySearch: activityData.handleActivitySearch,
    loadMoreActivities: activityData.loadMoreActivities,
    isLoadingMoreActivities: activityData.isLoadingMore,
    hasMoreActivityResults: activityData.hasMoreResults,
    isActivityLoading: activityData.isLoading,
    isTinyEmail,
    isTinyAlbert,
  };
};

export default useSelectRules;

interface UseFormSubmissionParams {
  node: any;
  formValues: any;
  updateNode: (params: { id: string; data: any }) => void;
  hideDrawer: () => void;
  isTinyEmail: boolean;
  id: string;
}

export const useFormSubmission = ({
  node,
  formValues,
  updateNode,
  hideDrawer,
  isTinyEmail,
  id,
}: UseFormSubmissionParams) => {
  const [saveRulesDisabled, setSaveRulesDisabled] = React.useState(true);

  const onSubmit = async (data: any) => {
    let nodeData: TriggerData = {
      ruleType: data.ruleType,
      source: data.source,
      event: data?.event,
    };

    if (data.ruleType === FORM_CONSTANTS.RULE_TYPES.ASSIGNED_AUDIENCE) {
      nodeData = {
        ...nodeData,
        event: FORM_CONSTANTS.EVENTS.CUSTOMER_ASSIGNED_TO_SEGMENT,
        source: (isTinyEmail
          ? FORM_CONSTANTS.SOURCES.TINYEMAIL
          : FORM_CONSTANTS.SOURCES.TINYALBERT) as "SHOPIFY" | "TINYEMAIL",
      };
    }

    if (data.ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY) {
      nodeData = {
        ...nodeData,
        source: (isTinyEmail
          ? FORM_CONSTANTS.SOURCES.TINYEMAIL
          : FORM_CONSTANTS.SOURCES.TINYALBERT) as "SHOPIFY" | "TINYEMAIL",
        activityType: FORM_CONSTANTS.ACTIVITY_TYPES.CAMPAIGN,
        activityId: data.activityId,
      };
    }

    if (data.ruleType === FORM_CONSTANTS.RULE_TYPES.CLEANED_CUSTOMER) {
      nodeData = {
        ...nodeData,
        event: FORM_CONSTANTS.EVENTS.CLEANED_CUSTOMER,
        source: (isTinyEmail
          ? FORM_CONSTANTS.SOURCES.TINYEMAIL
          : FORM_CONSTANTS.SOURCES.TINYALBERT) as "SHOPIFY" | "TINYEMAIL",
      };
    }

    const updateData: any = {
      ...nodeData,
      config: {
        configured: true,
      },
    };

    if (
      data.ruleType === FORM_CONSTANTS.RULE_TYPES.ASSIGNED_AUDIENCE ||
      data.ruleType === FORM_CONSTANTS.RULE_TYPES.CLEANED_CUSTOMER
    ) {
      updateData.audiences = data.audiences?.map((audience: any) => ({
        id: audience.id,
      }));
    }

    updateNode({
      id,
      data: updateData,
    });

    hideDrawer();
  };

  React.useEffect(() => {
    const nodeAudienceIds =
      node?.data?.audiences?.map((aud: any) => aud.id) || [];
    const formAudienceIds =
      formValues.audiences?.map((aud: any) => aud.id) || [];

    const audiencesChanged =
      nodeAudienceIds.length !== formAudienceIds.length ||
      formAudienceIds.some((id: string) => !nodeAudienceIds.includes(id || ""));

    const otherValuesChanged =
      formValues.ruleType !== (node?.data?.ruleType || "") ||
      formValues.source !== (node?.data?.source || "") ||
      formValues.event !== (node?.data?.event || "") ||
      formValues.activityId !== (node?.data?.activityId || "");

    setSaveRulesDisabled(!(audiencesChanged || otherValuesChanged));
  }, [node, formValues]);

  return {
    onSubmit,
    saveRulesDisabled,
  };
};
